import { useState } from 'react'
import Paper from 'components/report/Paper'
import Snippet from 'components/report/Snippet'
import CircleSpinner from 'components/misc/CircleSpinner'
import Fade, { FadeVisual } from 'components/misc/Fade'
import OpenerIcon from 'components/misc/OpenerIcon'
import Header from 'components/typography/Header'
import Expand from 'components/misc/Expand'
import Abstract from 'components/misc/Abstract'
import TypeIcon from 'components/misc/TypeIcon'
import Link from 'components/misc/Link'
import { ClassificationDetails } from './Citation'
import toTitleCase from 'lib/title-caser'
import { highlightText } from 'lib/highlight-text'

import styles from './styles/ReferencesList.css'

/**
 * calculatePunctuation for an item in a list given it's length
 * and the items position (index).
 */
const calculatePunctuation = (length, index) => {
  if (length - 1 === index) return ''
  if (index === length - 2) return ' and '
  return ', '
}

/**
 * referenceStatementMetadataCopy generates informative
 * copy about statements metadata such as type of citation and section
 * we have this since we are not allowed to show snippets.
 */
const referenceStatementMetadataCopy = (references) => {
  const typeCount = {}
  for (const reference of references) {
    if (reference.type) {
      if (reference.type in typeCount) {
        typeCount[reference.type] += 1
      } else {
        typeCount[reference.type] = 1
      }
    }
  }

  const typeCountEntries = Object.entries(typeCount)
  return (
    <>
      {' '}(
      {typeCountEntries.map(
        ([type, count], i) => `${count} ${type} reference${count > 1 ? 's' : ''}${calculatePunctuation(typeCountEntries.length, i)}`
      ).join('')}
      )
    </>
  )
}

const PaperCard = ({ originalReferences, references, paper, tally, textMatch, sourceDOI }) => {
  const displayTypes = {
    contradicting: 'contrasting'
  }

  const [expandReferences, setExpandReferences] = useState(false)
  const [abstractToggled, setAbstractToggled] = useState(false)
  const referencesWithStatements = references?.filter(cite => !!cite.snippet) || []
  const referencesWithTypes = references?.filter(cite => !!cite.type) || []
  return (
    <div className={styles.row}>
      <div className={styles.paperLayout}>
        <div className={styles.grid}>
          <div>
            {paper && (
              <Paper
                tally={tally}
                paper={paper}
                textMatch={textMatch}
                selfCites={references?.[0]?.selfCites}
              />
            )}
            <Expand show={!!(paper && abstractToggled)}>
              <Abstract className={styles.abstract} abstract={paper?.abstract} textMatch={textMatch} />
            </Expand>
          </div>
          <div className={styles.menu}>
            {!!paper?.abstract && (
              <a
                className={styles.textButton}
                tabIndex='0'
                onClick={() => setAbstractToggled(!abstractToggled)}
              >
                {abstractToggled ? 'hide abstract' : 'show abstract'}
              </a>
            )}
          </div>
        </div>
        {referencesWithTypes.length > 0 && <div className={styles.referenceSummary}>This publication is <strong>referenced {referencesWithTypes?.length - 1 > 0 ? `${referencesWithTypes?.length} times` : '1 time'}</strong>{referencesWithTypes?.length > 0 && referenceStatementMetadataCopy(referencesWithTypes)}.</div>}
        {referencesWithStatements.length > 0
          ? (
            <>
              <span className={styles.expandReferences} onClick={() => setExpandReferences(!expandReferences)} role='button' tabIndex={0}>
                <OpenerIcon show={expandReferences} />
                {
                  expandReferences
                    ? `Hide reference ${referencesWithStatements.length > 1 ? 'statements' : 'statement'}`
                    : `See how it was used (${referencesWithStatements.length} reference ${referencesWithStatements.length > 1 ? 'statements' : 'statement'})`
                }
              </span>
              <Expand show={expandReferences}>
                {
                  referencesWithStatements?.filter(cite => !!cite.snippet).map((cite, index) => (
                    <div key={index} className={styles.reference}>
                      <div className={styles.citeInfo}>
                        {cite?.section && <span className={styles.section}><b>Section</b>: {toTitleCase(cite.section)}</span>}
                        {originalReferences && `Statement #${originalReferences.findIndex(ref => ref?.refLocation === cite?.refLocation) + 1}:`}
                        {cite?.type && (
                          <span className={styles.typeInfo}>
                            <TypeIcon className={styles.icon} type={cite.type} />
                            <span className={styles.citationType}>{displayTypes[cite.type] || cite.type}</span>
                            <ClassificationDetails
                              type={cite.type}
                              typeConfidence={cite.typeConfidence}
                              expertClassified={!!cite.expertClassification}
                            />
                          </span>)}
                      </div>
                      {cite.snippet && <Snippet reportTarget={cite.target} snippet={highlightText(cite.snippet, textMatch)} />}
                    </div>
                  ))
                }
              </Expand>
            </>
            )
          : sourceDOI && (
            <Link blue underline to={`/reports/${paper.doi}?source_doi[0]=${sourceDOI}`}>
              See how it was used
            </Link>
          )}

      </div>
    </div>
  )
}

const ReferencesList = ({
  tallies,
  papers,
  citations,
  sorting,
  textMatch,
  sourceDOI
}) => {
  return (
    <div className={styles.citationsList}>
      <FadeVisual show={!sorting}>
        {papers
          .map((paper) =>
            <PaperCard
              key={`reference-${paper?.paper?.doi}`}
              originalReferences={citations}
              references={paper?.citations}
              paper={paper?.paper}
              tally={tallies ? tallies[paper?.paper?.doi] : paper?.tally}
              textMatch={textMatch}
              sourceDOI={sourceDOI}
            />
          )}
      </FadeVisual>
      <FadeVisual className={styles.loading} show={sorting}>
        <CircleSpinner />
      </FadeVisual>
      <Fade className={styles.row} show={!sorting && papers.length === 0}>
        <Header level={4}>No references</Header>
      </Fade>
    </div>
  )
}

export default ReferencesList
